/**
 * Slack's composer formatting (toolbar buttons and ⌘B / ⌘I / ⌘⇧X / ⌘⇧C / ⌘⌥⇧C / ⌘⇧U / ⌘⇧7 / ⌘⇧8 / ⌘⇧9) on a
 * markdown draft. The chat draft stays the text the server parses (apps/api richtext/parse: `*bold*`, `_italic_`,
 * `~strike~`, `` `code` ``, ``` fences, `[text](url)`, `- ` / `1. ` lists, `> ` quotes), so formatting = adding or
 * removing that syntax around the selection. Each kind toggles: applied again to formatted text it removes it.
 * Offsets are draft offsets; the result says where the selection goes.
 */

export type FormatKind = "bold" | "italic" | "strike" | "code" | "codeBlock" | "link" | "bulletList" | "orderedList" | "quote";

export const FORMAT_KINDS: readonly FormatKind[] = ["bold", "italic", "strike", "code", "codeBlock", "link", "bulletList", "orderedList", "quote"];

export type FormatEdit = { text: string; start: number; end: number };

const MARKERS = { bold: "*", italic: "_", strike: "~", code: "`" } as const;
const FENCE = "```";
/** What an empty link's address starts as (selected, ready to paste over). */
export const LINK_PLACEHOLDER = "https://";

type InlineKind = keyof typeof MARKERS;

function inline(text: string, start: number, end: number, kind: InlineKind): FormatEdit {
  const m = MARKERS[kind];
  const n = m.length;
  // Markers right outside the selection (or around the caret, an empty pair): unwrap.
  if (start >= n && text.slice(start - n, start) === m && text.slice(end, end + n) === m) {
    return { text: text.slice(0, start - n) + text.slice(start, end) + text.slice(end + n), start: start - n, end: end - n };
  }
  const sel = text.slice(start, end);
  // The selection itself is wrapped (`*word*` selected): unwrap it.
  if (sel.length > 2 * n && sel.startsWith(m) && sel.endsWith(m)) {
    const inner = sel.slice(n, -n);
    return { text: text.slice(0, start) + inner + text.slice(end), start, end: start + inner.length };
  }
  if (start === end && text.slice(end, end + n) === m) {
    // Typing inside a pair (`*bold|*`): the shortcut again steps out of it, like turning bold off in Slack.
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    if (text.slice(lineStart, start).split(m).length % 2 === 0) return { text, start: end + n, end: end + n };
  }
  if (start === end) {
    // An empty pair with the caret between: typing goes inside.
    return { text: text.slice(0, start) + m + m + text.slice(end), start: start + n, end: start + n };
  }
  // Wrap each line's text without its surrounding spaces (a marker next to a space doesn't parse).
  const lines = sel.split("\n");
  const out = lines
    .map((line) => {
      const body = line.trim();
      if (body === "") return line;
      const lead = line.length - line.trimStart().length;
      return line.slice(0, lead) + m + body + m + line.slice(lead + body.length);
    })
    .join("\n");
  const first = lines[0]!;
  const last = lines.at(-1)!;
  // The words stay selected (inside the markers), so the same shortcut again unwraps them.
  const selStart = start + (first.trim() === "" ? 0 : first.length - first.trimStart().length + n);
  const selEnd = end + (out.length - sel.length) - (last.trim() === "" ? 0 : last.length - last.trimEnd().length + n);
  return { text: text.slice(0, start) + out + text.slice(end), start: selStart, end: selEnd };
}

function codeBlock(text: string, start: number, end: number): FormatEdit {
  const sel = text.slice(start, end);
  // Already fenced (fences just outside, on their own lines, or selected along with the text): unfence.
  const before = text.slice(0, start);
  const after = text.slice(end);
  if (before.endsWith(FENCE + "\n") && after.startsWith("\n" + FENCE)) {
    const s = start - FENCE.length - 1;
    return { text: text.slice(0, s) + sel + text.slice(end + FENCE.length + 1), start: s, end: s + sel.length };
  }
  if (sel.length >= 2 * FENCE.length + 2 && sel.startsWith(FENCE + "\n") && sel.endsWith("\n" + FENCE)) {
    const inner = sel.slice(FENCE.length + 1, -(FENCE.length + 1));
    return { text: before + inner + after, start, end: start + inner.length };
  }
  // A fence must start its own line, and what follows the closing one goes on the next line.
  const lead = start > 0 && text[start - 1] !== "\n" ? "\n" : "";
  const trail = end < text.length && text[end] !== "\n" ? "\n" : "";
  const open = `${lead}${FENCE}\n`;
  const out = `${open}${sel}\n${FENCE}${trail}`;
  return { text: before + out + after, start: start + open.length, end: start + open.length + sel.length };
}

function link(text: string, start: number, end: number): FormatEdit {
  const sel = text.slice(start, end);
  // `[label](url)` selected: back to the label.
  const md = /^\[([^\]\n]+)\]\(([^\s()]*)\)$/.exec(sel);
  if (md !== null) {
    const label = md[1]!;
    return { text: text.slice(0, start) + label + text.slice(end), start, end: start + label.length };
  }
  // A bare address selected: it already links (autolink); make it the label too, ready to rename.
  if (/^https?:\/\/\S+$/i.test(sel)) {
    const out = `[${sel}](${sel})`;
    return { text: text.slice(0, start) + out + text.slice(end), start: start + 1, end: start + 1 + sel.length };
  }
  if (start === end) {
    // `[](https://)`: the caret goes in the label.
    const out = `[](${LINK_PLACEHOLDER})`;
    return { text: text.slice(0, start) + out + text.slice(end), start: start + 1, end: start + 1 };
  }
  // `[label](https://)` with the address placeholder selected (paste the link over it).
  const out = `[${sel}](${LINK_PLACEHOLDER})`;
  const at = start + sel.length + 3;
  return { text: text.slice(0, start) + out + text.slice(end), start: at, end: at + LINK_PLACEHOLDER.length };
}

const ORDERED_RE = /^(\s*)\d+[.)] /;
const BULLET_RE = /^(\s*)[-*•] /;
const QUOTE_RE = /^(\s*)> ?/;

type LineKind = "bulletList" | "orderedList" | "quote";

function prefixRe(kind: LineKind): RegExp {
  return kind === "bulletList" ? BULLET_RE : kind === "orderedList" ? ORDERED_RE : QUOTE_RE;
}

function lines(text: string, start: number, end: number, kind: LineKind): FormatEdit {
  const from = text.lastIndexOf("\n", start - 1) + 1;
  const nl = text.indexOf("\n", end > start && text[end - 1] === "\n" ? end - 1 : end);
  const to = nl < 0 ? text.length : nl;
  const block = text.slice(from, to).split("\n");
  const re = prefixRe(kind);
  // A list may sit inside a quote (`> - a`): list prefixes are read and written after the line's quote marker.
  const split = (l: string): [string, string] => {
    if (kind === "quote") return ["", l];
    const q = QUOTE_RE.exec(l);
    return q === null ? ["", l] : [q[0], l.slice(q[0].length)];
  };
  const filled = block.filter((l) => l.trim() !== "");
  const allHave = filled.length > 0 && filled.every((l) => re.test(split(l)[1]));
  let n = 0;
  const out = block.map((l) => {
    const [quote, rest] = split(l);
    if (allHave) return quote + rest.replace(re, "$1");
    if (l.trim() === "" && block.length > 1) return l;
    if (kind === "quote") return `> ${l}`;
    // Switching list kinds: drop the other list's prefix first.
    const bare = rest.replace(BULLET_RE, "$1").replace(ORDERED_RE, "$1");
    const lead = bare.length - bare.trimStart().length;
    n += 1;
    return quote + bare.slice(0, lead) + (kind === "bulletList" ? "- " : `${n}. `) + bare.slice(lead);
  });
  const joined = out.join("\n");
  const result = text.slice(0, from) + joined + text.slice(to);
  // Only a caret (Slack): it stays by the same character while the line's prefix comes or goes (on an empty line,
  // after the new prefix); nothing gets selected. A selection keeps the formatted lines selected.
  if (start === end && block.length === 1) {
    const caret = Math.max(from, start + (joined.length - block[0]!.length));
    return { text: result, start: caret, end: caret };
  }
  return { text: result, start: from, end: from + joined.length };
}

/** Applies (or removes) one kind of formatting at [start, end) of the draft. */
export function applyFormat(text: string, start: number, end: number, kind: FormatKind): FormatEdit {
  const s = Math.max(0, Math.min(start, end, text.length));
  const e = Math.min(text.length, Math.max(start, end));
  switch (kind) {
    case "bold":
    case "italic":
    case "strike":
    case "code":
      return inline(text, s, e, kind);
    case "codeBlock":
      return codeBlock(text, s, e);
    case "link":
      return link(text, s, e);
    case "bulletList":
    case "orderedList":
    case "quote":
      return lines(text, s, e, kind);
  }
}
